import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { FileText, Download, Edit, CheckCircle, Clock, AlertCircle, Shield, Eye } from 'lucide-react';
import { documentsAPI, signaturesAPI } from '../services/api';
import { toast } from 'sonner';
import SignatureModal from './SignatureModal';
import { useAuth } from '../context/AuthContext';

const DocumentList = ({ dealId, userRole }) => {
  const { user } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [signatures, setSignatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [signingDoc, setSigningDoc] = useState(null);
  const [viewingSignature, setViewingSignature] = useState(null);

  useEffect(() => {
    fetchDocuments();
    fetchSignatures();
  }, [dealId]);

  const fetchDocuments = async () => {
    try {
      const response = await documentsAPI.getDealDocuments(dealId);
      setDocuments(response.data);
    } catch (error) {
      console.error('Failed to load documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSignatures = async () => {
    try {
      const response = await signaturesAPI.getDealSignatures(dealId);
      setSignatures(response.data);
    } catch (error) {
      console.error('Failed to load signatures:', error);
    }
  };

  const handleDownload = async (docId, fileName) => {
    try {
      const response = await documentsAPI.download(docId);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Document downloaded');
    } catch (error) {
      toast.error('Failed to download document');
    }
  };

  const handleSign = (doc) => {
    setSigningDoc(doc);
  };

  const handleSaveSignature = async (signatureInfo) => {
    try {
      await signaturesAPI.signDocument(
        signingDoc.id,
        signatureInfo.signatureData,
        signatureInfo.signatureType,
        signatureInfo.signerName
      );
      toast.success('Document signed successfully!');
      setSigningDoc(null);
      fetchDocuments();
      fetchSignatures();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to sign document');
    }
  };

  const getDocumentSignatures = (docId) => {
    return signatures.filter(s => s.document_id === docId);
  };

  const hasUserSigned = (docId) => {
    return signatures.some(s => s.document_id === docId && s.signer_id === user?.id);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'signed':
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'sent':
        return <Clock className="w-4 h-4 text-yellow-600" />;
      default:
        return <AlertCircle className="w-4 h-4 text-slate-400" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'signed':
        return 'bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-400 dark:border-green-800';
      case 'sent':
        return 'bg-yellow-100 text-yellow-700 border-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-400 dark:border-yellow-800';
      default:
        return 'bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:border-slate-700';
    }
  };

  // Signature Modal
  if (signingDoc) {
    return (
      <SignatureModal
        onSave={handleSaveSignature}
        onCancel={() => setSigningDoc(null)}
        signerName={user?.name || ''}
      />
    );
  }

  return (
    <Card className="glass-effect border-slate-200 dark:border-slate-700 animate-slide-in-up">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <div className="w-10 h-10 gradient-accent rounded-lg flex items-center justify-center">
            <FileText className="w-5 h-5 text-white" />
          </div>
          Documents & Signatures
        </CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-center py-8">
            <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto" />
            <p className="text-slate-500 dark:text-slate-400 mt-2">Loading documents...</p>
          </div>
        ) : documents.length === 0 ? (
          <div className="text-center py-12">
            <FileText className="w-12 h-12 text-slate-400 mx-auto mb-3" />
            <p className="text-slate-600 dark:text-slate-400">No documents yet</p>
            <p className="text-sm text-slate-500 dark:text-slate-500 mt-1">
              Upload documents above to get started
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {documents.map((doc) => {
              const docSignatures = getDocumentSignatures(doc.id);
              const userHasSigned = hasUserSigned(doc.id);
              
              return (
                <div
                  key={doc.id}
                  className="p-4 border border-slate-200 dark:border-slate-700 rounded-lg hover:border-blue-300 dark:hover:border-blue-600 transition-all"
                  data-testid={`document-${doc.id}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-start gap-3 flex-1">
                      <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-500 rounded-lg flex items-center justify-center flex-shrink-0">
                        <FileText className="w-5 h-5 text-white" />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-semibold text-slate-900 dark:text-slate-100">{doc.file_name}</h4>
                        <p className="text-sm text-slate-600 dark:text-slate-400">
                          {doc.doc_type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {getStatusIcon(doc.status)}
                      <span className={`px-2 py-1 text-xs font-medium rounded border ${getStatusColor(doc.status)}`}>
                        {doc.status.toUpperCase()}
                      </span>
                    </div>
                  </div>

                  {/* Signatures Section */}
                  {docSignatures.length > 0 && (
                    <div className="mb-3 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                      <div className="flex items-center gap-2 mb-2">
                        <Shield className="w-4 h-4 text-green-600" />
                        <span className="text-sm font-medium text-green-700 dark:text-green-400">
                          Signatures ({docSignatures.length})
                        </span>
                      </div>
                      <div className="space-y-2">
                        {docSignatures.map((sig) => (
                          <div key={sig.id} className="flex items-center justify-between text-sm">
                            <div className="flex items-center gap-2">
                              <CheckCircle className="w-3 h-3 text-green-600" />
                              <span className="text-slate-700 dark:text-slate-300">{sig.signer_name}</span>
                              <span className="text-slate-500 dark:text-slate-500">({sig.signature_type})</span>
                            </div>
                            <span className="text-slate-500 dark:text-slate-500 text-xs">
                              {new Date(sig.signed_at).toLocaleDateString()} at {new Date(sig.signed_at).toLocaleTimeString()}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDownload(doc.id, doc.file_name)}
                      className="flex-1"
                      data-testid={`download-${doc.id}`}
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download
                    </Button>
                    {!userHasSigned && (
                      <Button
                        size="sm"
                        onClick={() => handleSign(doc)}
                        className="flex-1 gradient-primary border-0"
                        data-testid={`sign-${doc.id}`}
                      >
                        <Edit className="w-4 h-4 mr-2" />
                        Sign Document
                      </Button>
                    )}
                    {userHasSigned && (
                      <Button
                        size="sm"
                        variant="outline"
                        disabled
                        className="flex-1 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 border-green-200 dark:border-green-800"
                      >
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Signed
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* E-Signature Notice */}
        <div className="mt-6 p-4 bg-slate-50 dark:bg-slate-800/50 rounded-lg border border-slate-200 dark:border-slate-700">
          <div className="flex items-start gap-3">
            <Shield className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-slate-700 dark:text-slate-300">Secure E-Signatures</p>
              <p className="text-xs text-slate-500 dark:text-slate-500 mt-1">
                All signatures are electronically captured with timestamp, IP address logging, and document integrity verification for legal compliance.
              </p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default DocumentList;
